"use client";

import React, { useEffect, useState } from "react";
import RequestItemForm from "@/components/requestItem/RequestItemForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { usePopup } from '@/contexts/PopupContext';

interface RequestItemFormPopupProps {
  currentPopupType?: string | null;
  isContextOpen?: boolean;
  onClose?: () => void;
  disableAutoOpen?: boolean;
  initialData?: {
    title?: string;
    description?: string;
    serviceId?: string;
    categoryId?: string;
    subCategoryId?: string;
    domainCategoryId?: string;
  };
}

export default function RequestItemFormPopup({ 
  currentPopupType,
  isContextOpen,
  onClose,
  disableAutoOpen = false,
  initialData 
}: RequestItemFormPopupProps) {
  const { setPopupOpen, setPopupType } = usePopup();
  const [homepageDismissed, setHomepageDismissed] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

 
  // Auto-open homepage popup when component mounts (only once, unless dismissed)
  useEffect(() => {
    if (!disableAutoOpen && !isContextOpen && !homepageDismissed) {   
      setPopupType('homepage');
      setPopupOpen(true);
    }
  }, [disableAutoOpen, isContextOpen, homepageDismissed, setPopupOpen, setPopupType]);

  const handleClose = () => {
    if (isFormSubmitting) return;
       
    // If closing homepage popup, mark it as dismissed
    if (currentPopupType === 'homepage') {
      setHomepageDismissed(true);
    }
    
    // Clear sessionStorage when closing carousel popup
    if (currentPopupType === 'carousel') {
      sessionStorage.removeItem('selectedServiceData');
    }
    
    // Call the onClose callback to reset context state
    if (onClose) {
      onClose(); // This calls resetPopup
    }
  };

  // Determine what to show based on context state
  const shouldShow = isContextOpen && (currentPopupType === 'homepage' || currentPopupType === 'carousel');
  const popupId = currentPopupType || 'homepage';


  if (!shouldShow) {
   
    return null;
  }

  return (
    <div className="fixed inset-0 z-[1200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="relative w-full max-w-[600px] md:max-w-[700px] lg:max-w-[780px] bg-white rounded shadow-lg overflow-hidden h-[50vh] min-h-[50vh] flex flex-col">
        <button
          type="button"
          aria-label="Close"
          onClick={handleClose}
          disabled={isFormSubmitting}
          className="absolute z-[1300] top-2 right-2 p-1 bg-white text-red-600 shadow transition-all focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faTimes} className="text-base" />
        </button>

        <div className="h-10 md:h-12 bg-[var(--primary-color)] text-white text-md flex items-center pl-4 font-semibold flex-shrink-0">
          Request Any Auto Item Here!
        </div>

        <div className="p-4 md:p-6 overflow-y-auto flex-1 request-item-form-modal">
          <RequestItemForm 
            formClassName="h-full border border-gray-300" 
            formId={`popup-${popupId}`}
            onSubmittingChange={setIsFormSubmitting}
            initialData={initialData}
          />
        </div>

        <style jsx>{`
          .request-item-form-modal :global(> div) {
            position: static !important;
            top: 0 !important;
            height: auto !important;
          }
          .request-item-form-modal :global(.triangle) {
            display: none !important;
          }
        `}</style>
      </div>
    </div>
  );
}